<template>
  <div class="form-section mb-0">
    <div class="row">

      <div class="col-md-6">
        <div class="form-group">
          <label>Sueldo base *</label>
          <input
            type="number"
            v-model="form.baseSalary"
            autocomplete="off"
            placeholder="Ingresar sueldo base"
            class="custom-form-control"
            :class="{ 'custom-form-control-error': v.baseSalary.$error }"
          />
          <FormError
            v-if="v.baseSalary.$error && !v.baseSalary.required"
            message="Sueldo base es requerido"
          />
          <FormError
            v-if="v.baseSalary.$error && !v.baseSalary.numeric"
            message="El campo debe ser numérico"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label>Moneda</label>
          <FormSelect
            id="salaryCurrency"
            v-model="form.salaryCurrency"
            :items="currencyList"
            defaultOption="Elegir moneda"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label>Régimen laboral *</label>
          <FormSelect
            id="laborRegime"
            v-model="form.laborRegime"
            :items="laborRegimeList"
            defaultOption="Elegir régimen"
            :showError="v.laborRegime.$error && !v.laborRegime.required"
          />
          <FormError
            v-if="v.laborRegime.$error && !v.laborRegime.required"
            message="Régimen laboral es requerido"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label>Categoría ocupacional</label>
          <FormSelect
            id="occupationalCategory"
            v-model="form.occupationalCategory"
            :items="occupationalCategoryList"
            defaultOption="Elegir categoría"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label>Tipo de trabajador</label>
          <FormSelect
            id="workerType"
            v-model="form.workerType"
            :items="workerTypeList"
            defaultOption="Elegir tipo"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label>Jornada laboral</label>
          <FormSelect
            id="workSchedule"
            v-model="form.workSchedule"
            :items="workScheduleList"
            defaultOption="Elegir jornada"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label>Horas semanales</label>
          <input
            type="number"
            v-model="form.weeklyHours"
            autocomplete="off"
            placeholder="48"
            class="custom-form-control"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label>Situación especial</label>
          <FormSelect
            id="specialSituation"
            v-model="form.specialSituation"
            :items="specialSituationList"
            defaultOption="Elegir situación"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label>Email corporativo</label>
          <input
            type="email"
            v-model.trim="form.corporateEmail"
            autocomplete="off"
            placeholder="Ingresar email corporativo"
            class="custom-form-control"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group d-flex align-items-center mt-md-4 pt-md-1">
          <label class="mb-0 mr-3">Asignación familiar</label>
          <b-form-checkbox
            v-model="form.hasFamilyAllowance"
            name="hasFamilyAllowance"
            switch
            size="lg"
          />
        </div>
      </div>

      <!-- Resumen de hijos para asignación familiar -->
      <div class="col-12" v-if="form.hasFamilyAllowance">

        <!-- Caso A: tiene hijos calificados -->
        <template v-if="dependentSummary.qualifiedChildren > 0">
          <div class="table-responsive mb-2">
            <table class="custom-table w-100">
              <thead>
                <tr>
                  <th>NOMBRE</th>
                  <th>DOCUMENTO</th>
                  <th>EDAD</th>
                  <th>ESTADO AF</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="child in dependentSummary.children" :key="child.id">
                  <td>{{ child.fullName }}</td>
                  <td>{{ getDocTypeLabel(child.documentType) }} {{ child.documentNumber }}</td>
                  <td>{{ child.age }} años</td>
                  <td>
                    <span :class="['badge', getFamilyStatusColor(child.familyAllowanceStatus)]">
                      {{ getFamilyStatusLabel(child.familyAllowanceStatus) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="alert alert-success py-2 small mb-3">
            <i class="ri-check-line mr-1"></i>
            {{ dependentSummary.qualifiedChildren }} hijo(s) calificado(s) para asignación familiar (10% RMV = S/ 113.00)
          </div>
        </template>

        <!-- Caso B: tiene hijos pero ninguno califica -->
        <template v-else-if="dependentSummary.totalChildren > 0">
          <div class="table-responsive mb-2">
            <table class="custom-table w-100">
              <thead>
                <tr>
                  <th>NOMBRE</th>
                  <th>DOCUMENTO</th>
                  <th>EDAD</th>
                  <th>ESTADO AF</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="child in dependentSummary.children" :key="child.id">
                  <td>{{ child.fullName }}</td>
                  <td>{{ getDocTypeLabel(child.documentType) }} {{ child.documentNumber }}</td>
                  <td>{{ child.age }} años</td>
                  <td>
                    <span :class="['badge', getFamilyStatusColor(child.familyAllowanceStatus)]">
                      {{ getFamilyStatusLabel(child.familyAllowanceStatus) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="alert alert-warning py-2 small mb-3">
            <i class="ri-alert-line mr-1"></i>
            Ninguno de los hijos registrados cumple las condiciones para asignación familiar. Verifique las edades y constancias de estudio.
          </div>
        </template>

        <!-- Caso C: no tiene hijos registrados -->
        <template v-else>
          <div class="alert alert-light py-2 small mb-3 border">
            <i class="ri-information-line mr-1"></i>
            No hay hijos registrados para este colaborador.
            <router-link
              v-if="employeeId"
              :to="{ name: 'employee-detail', params: { id: String(employeeId) }, query: { tab: 'familia' } }"
              class="ml-1"
            >Ir a Ficha del colaborador > Familia</router-link>
          </div>
        </template>

      </div>

    </div>

    <!-- Tipos de planilla -->
    <hr class="my-3" />
    <h6 class="text-muted mb-3">Tipos de planilla</h6>
    <div class="row" v-if="payrollTypes.length > 0">
      <div class="col-md-6" v-for="pt in payrollTypes" :key="pt.id">
        <div class="form-group d-flex align-items-center mb-2">
          <b-form-checkbox
            :value="pt.id"
            v-model="form.payrollTypeIds"
            class="mr-2"
          />
          <span>{{ pt.name }}</span>
          <span :class="`badge ml-2 ${getFrequencyBadge(pt.frequency)}`" style="font-size: 0.7rem;">
            {{ getFrequencyLabel(pt.frequency) }}
          </span>
          <span :class="`badge ml-1 ${pt.taxCategory === 'FIFTH' ? 'badge-info' : 'badge-secondary'}`" style="font-size: 0.7rem;">
            {{ pt.taxCategory === 'FIFTH' ? '5ta' : '4ta' }}
          </span>
        </div>
      </div>
    </div>
    <p v-else class="text-muted small">No hay tipos de planilla configurados.</p>

  </div>
</template>

<script>
import { FormError, FormSelect } from 'wize-admin'
import { Constants } from '@/core/utils'

export default {
  components: { FormError, FormSelect },
  props: {
    form:             { type: Object, required: true },
    v:                { type: Object, required: true },
    dependentSummary: { type: Object, default: () => ({ totalChildren: 0, qualifiedChildren: 0, hasQualifiedChildren: false, children: [] }) },
    employeeId:       { type: Number, default: null },
    payrollTypes:     { type: Array,  default: () => [] },
  },
  data() {
    return {
      currencyList:            Object.values(Constants.SalaryCurrency),
      laborRegimeList:         Object.values(Constants.LaborRegime),
      occupationalCategoryList: Object.values(Constants.OccupationalCategory),
      workerTypeList:          Object.values(Constants.WorkerType),
      workScheduleList:        Object.values(Constants.WorkSchedule),
      specialSituationList:    Object.values(Constants.SpecialSituation),
    }
  },
  methods: {
    getDocTypeLabel(type) {
      const found = Object.values(Constants.IdentifierType).find(t => String(t.key) === String(type))
      return found ? found.label : type
    },
    getFamilyStatusColor(status) {
      const found = Object.values(Constants.FamilyAllowanceStatus).find(s => s.key === status)
      return found ? found.color : 'badge-gray'
    },
    getFamilyStatusLabel(status) {
      const found = Object.values(Constants.FamilyAllowanceStatus).find(s => s.key === status)
      return found ? found.label : (status || '-')
    },
    getFrequencyLabel(freq) {
      const map = { WEEKLY: 'Semanal', BIWEEKLY: 'Quincenal', MONTHLY: 'Mensual' }
      return map[freq] || freq
    },
    getFrequencyBadge(freq) {
      const map = { WEEKLY: 'badge-warning', BIWEEKLY: 'badge-info', MONTHLY: 'badge-green' }
      return map[freq] || 'badge-gray'
    },
  },
}
</script>

<style lang="stylus" scoped></style>
